import { useCallback, useEffect, useRef, useState, RefObject } from 'react';
import { fetchChatHistory, ChatHistoryRow } from '../../../services/api/chatHistory.api';
import { Message } from '../types';
import { HISTORY_PAGE_SIZE } from '../constants';

// The history of the chat currently open. A new one is created each time a
// chat is opened and cancelled when the user leaves it, so a reply that
// arrives late for a chat the user has left can't change the chat on screen.
interface HistorySession {
  sessionId: string;
  // Cancels this chat's history requests; a cancelled request changes nothing.
  controller: AbortController;
  // Pages actually shown (newest first). "Load older" asks for the next one
  // and only counts it once it's on screen, so a failed page is asked for
  // again on the next scroll to the top instead of being skipped.
  loadedPages: number;
  // False once the beginning of the conversation has been reached.
  hasMore: boolean;
}

interface Options {
  email: string | null;
  urlSessionId: string | undefined;
  chatMessagesRef: RefObject<HTMLDivElement | null>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  // Injected rather than imported directly — these belong to other hooks
  // (useTurnFeedback, useWebSocket, generatedImageCache) and this hook only
  // needs to call them when a history session switch happens, not own them.
  resetTurnRatings: () => void;
  resetActiveTurn: () => void;
  clearGeneratedImageCache: () => void;
}

// Owns loading a session's chat history page-by-page: initial load when
// navigating to an existing session, and lazy-loading older pages as the
// user scrolls to the top. Also carries the session-switch cleanup that
// naturally belongs with "the user just switched to a different history
// session" (clearing turn ratings/active-turn/generated-image cache) — this
// hook's job is really "history session lifecycle," not just pagination.
export const useChatHistoryPagination = ({
  email,
  urlSessionId,
  chatMessagesRef,
  messagesEndRef,
  setMessages,
  resetTurnRatings,
  resetActiveTurn,
  clearGeneratedImageCache,
}: Options) => {
  // historySessionRef: the open chat's history (see HistorySession)
  // isLoadingHistory: shows a loading indicator at the top while fetching
  const historySessionRef = useRef<HistorySession | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Leaving a chat: cancel its history requests and forget it.
  const endHistorySession = useCallback(() => {
    historySessionRef.current?.controller.abort();
    historySessionRef.current = null;
    setIsLoadingHistory(false);
  }, []);

  // ── History: expand API rows into Message pairs ───────────────────────────
  // The API returns one row per exchange: { chat_query, chat_response, files, created_at }
  // We expand each row into two Message objects to fit the existing Message format.
  // IDs are made unique by combining created_at + index to avoid collisions.
  //
  // Covers both doc and generated-image rows the same way, since both come
  // back inside the same `files` array — a generated-image row's
  // chat_response is just raw text like "Here is your image (image_id=...)",
  // which MessageList suppresses in favor of rendering the image once
  // `files` is attached, exactly like it does for the equivalent live-WS
  // response (useWebSocket.tsx).
  const expandHistoryRows = (rows: ChatHistoryRow[]): Message[] => {
    const expanded: Message[] = [];
    rows.forEach((row, i) => {
      // User side of the exchange
      expanded.push({
        id: `history-user-${row.created_at}-${i}`,
        content: row.chat_query,
        sender: 'user',
        timestamp: row.created_at,
      });

      // Assistant side of the exchange
      expanded.push({
        id: `history-assistant-${row.created_at}-${i}`,
        content: row.chat_response,
        sender: 'assistant',
        timestamp: row.created_at,
        isMarkdown: true,
        files: row.files && row.files.length > 0 ? row.files : undefined,
        turnId: row.turn_id,
      });
    });
    return expanded;
  };

  // ── History: fetch a single page ─────────────────────────────────────────
  // Page 1 → replaces messages (initial load on navigation to /chat/:sessionId)
  // Page 2+ → prepends messages (lazy load on scroll-to-top)
  // Scroll position is preserved after prepend so the view doesn't jump.
  // Once the user has left `session`'s chat, its request is cancelled and
  // nothing here touches the screen or the new chat's state.
  const loadHistoryPage = useCallback(async (session: HistorySession, page: number) => {
    if (!email || !session.hasMore) return;
    const { signal } = session.controller;

    setIsLoadingHistory(true);
    try {
      const rows = await fetchChatHistory(email, session.sessionId, page, signal);
      if (signal.aborted) return;

      // No rows returned means we've loaded the full history
      if (rows.length === 0) {
        session.hasMore = false;
        return;
      }

      const newMessages = expandHistoryRows([...rows].reverse());

      if (page === 1) {
        // Initial load — replace empty state with history,
        // then scroll to bottom so the user sees the most recent messages.
        // Deliberately does NOT auto-resolve a SAS URL for any historical
        // file (unlike a live WS response, which does — see onFilesReceived)
        // — pre-fetching a link for a file nobody's asked to see yet while
        // just browsing history is wasted work. Every file still gets its
        // own Preview card (from expandHistoryRows attaching `files`); the
        // SAS lookup only happens once one is actually clicked.
        setMessages(newMessages);
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
        });
      } else {
        // Lazy load — prepend older messages above the existing ones.
        // Save scroll height before prepend so we can restore position after.
        const container = chatMessagesRef.current;
        const scrollHeightBefore = container?.scrollHeight ?? 0;

        setMessages((prev) => [...newMessages, ...prev]);

        // Restore scroll position so the viewport doesn't jump upward
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - scrollHeightBefore;
          }
        });
      }

      session.loadedPages = page;
      // Fewer rows than the page size means we've reached the beginning
      if (rows.length < HISTORY_PAGE_SIZE) {
        session.hasMore = false;
      }
    } catch (e) {
      if (signal.aborted) return;
      console.error('[Chat] Failed to load history:', e);
    } finally {
      if (!signal.aborted) setIsLoadingHistory(false);
    }
  }, [email, chatMessagesRef, messagesEndRef, setMessages]);

  // ── History: load page 1 when navigating to an existing session ───────────
  // Only fires when urlSessionId is present (i.e. /chat/:sessionId route).
  // Never fires for new chats since urlSessionId is undefined (that path is
  // covered by the chat:new handler in Chat.tsx instead, via resetHistoryPagination).
  useEffect(() => {
    if (!email || !urlSessionId) return;

    const session: HistorySession = {
      sessionId: urlSessionId,
      controller: new AbortController(),
      loadedPages: 0,
      hasMore: true,
    };
    historySessionRef.current = session;

    setMessages([]); // clear any stale messages from a previous session
    resetTurnRatings(); // ratings are session-scoped too — see useTurnFeedback's own comment
    // Whatever was in flight for the chat we're leaving is now orphaned —
    // the backend isn't cancelled, so without this its eventual response
    // would otherwise land in the message list we just switched to.
    resetActiveTurn();
    // Leaving whatever chat was previously open — its generated images
    // (if any) shouldn't stay resident in memory once we're viewing a
    // different session.
    clearGeneratedImageCache();
    void loadHistoryPage(session, 1);

    // Leaving this chat (another one opened, a new chat, or unmount).
    return () => {
      if (historySessionRef.current === session) endHistorySession();
      else session.controller.abort();
    };
  }, [email, urlSessionId]);

  // ── History: scroll-to-top triggers next page ────────────────────────────
  // When the user scrolls within 80px of the top, fetch the next page.
  // The isLoadingHistory check prevents duplicate fetches while one is in flight.
  useEffect(() => {
    const container = chatMessagesRef.current;
    if (!container || !urlSessionId) return;

    const handleScroll = () => {
      const session = historySessionRef.current;
      if (session && container.scrollTop < 80 && !isLoadingHistory && session.hasMore) {
        void loadHistoryPage(session, session.loadedPages + 1);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [urlSessionId, isLoadingHistory, loadHistoryPage]);

  // Used by the chat:new handler — ends the open chat's history (cancelling
  // anything still loading for it) so the new chat starts clean.
  const resetHistoryPagination = endHistorySession;

  return { isLoadingHistory, resetHistoryPagination };
};
